import {
    Autocomplete,
    Box,
    Checkbox,
    Chip,
    FormControlLabel,
    FormGroup,
    Grid,
    IconButton,
    Link,
    Modal,
    Slider,
    Stack,
    TextField,
} from "@mui/material";
import { CATEGORY_COLORS, COLORS, MODEL_PROPTYPES } from "../../../constants";
import {
    Close,
    Image,
    OpenInNew,
    Undo,
    ZoomIn,
    ZoomOut,
} from "@mui/icons-material";
import {
    TransformComponent,
    TransformWrapper,
    useControls,
} from "react-zoom-pan-pinch";
import { useEffect, useMemo, useState } from "react";

import { AnnotatedCanvas } from "../../../components/AnnotatedCanvas";
import { BACKEND_ROUTES } from "../../../backendRoutes";
import { FetchErrorAlert } from "../../../components/FetchErrorAlert";
import { LightTooltip } from "../../../components/LightTooltip";
import PropTypes from "prop-types";
import { TraitValueTable } from "./TraitValueTable";
import { formatDateTime } from "../../../utils/formatTime";
import useSWR from "swr";

EntityFileVisualizer.propTypes = {
    entityFile: PropTypes.shape(MODEL_PROPTYPES.EntityFile).isRequired,
    entity: PropTypes.shape(MODEL_PROPTYPES.Entity).isRequired,
    xTrait: PropTypes.string,
    yTrait: PropTypes.string,
    labels: PropTypes.array,
    label: PropTypes.string,
    setLabel: PropTypes.func,
};

function initCategoryVisuParams(categories) {
    return categories.map((category) => {
        const categoryColor =
            CATEGORY_COLORS[category.name?.toLowerCase()] ?? "red";
        return {
            ...category,
            checked: true,
            lineWidth: 3,
            color: categoryColor.startsWith("#")
                ? categoryColor
                : COLORS[categoryColor],
        };
    });
}

const Controls = () => {
    const { zoomIn, zoomOut, resetTransform } = useControls();

    return (
        <Box
            sx={{
                background: "white",
                p: 0.5,
                borderRadius: 2,
            }}
        >
            <IconButton onClick={() => zoomOut()}>
                <ZoomOut />
            </IconButton>
            <IconButton onClick={() => zoomIn()}>
                <ZoomIn />
            </IconButton>
            <IconButton onClick={() => resetTransform()}>
                <Undo />
            </IconButton>
        </Box>
    );
};

/**
 * It is assumed that if entityFile.annotation.categories exists, it is an array of objects,
 * each of which contains an id and a name. (Otherwise the page might crash)
 */
export function EntityFileVisualizer({
    entityFile,
    entity,
    xTrait,
    yTrait,
    labels,
    label,
    setLabel,
}) {
    const [openModal, setOpenModal] = useState(false);

    const [categoryVisuParams, setCategoryVisuParams] = useState(null);

    const imageData = useMemo(
        () => ({
            roi: entityFile.roi,
            annotations: entityFile.annotation?.annotations,
            categories: entityFile.annotation?.categories,
        }),
        [entityFile]
    );

    // Set category visu params on entityfile change
    useEffect(() => {
        setCategoryVisuParams(
            entityFile.annotation?.categories
                ? initCategoryVisuParams(entityFile.annotation.categories)
                : null
        );
    }, [entityFile]);

    const { data: presignedUrl, error: presignedUrlFetchError } = useSWR(
        `${BACKEND_ROUTES.FILES}/${entityFile.fileUuid}/${BACKEND_ROUTES.FILE_URL}`
    );

    const mergedError = presignedUrlFetchError;

    if (mergedError) return <FetchErrorAlert error={mergedError} />;

    return (
        <>
            <Modal
                sx={{ m: 5 }}
                open={openModal}
                onClose={() => setOpenModal(false)}
            >
                <Grid
                    container
                    sx={{
                        background: "rgba(255,255,255,0.5)",
                        borderRadius: 6,
                        p: 1,
                    }}
                >
                    <Grid item sx={{ p: 1 }}>
                        <TransformWrapper
                            smooth
                            initialScale={0.1}
                            minScale={0.1}
                            maxScale={100}
                        >
                            <>
                                <Box
                                    sx={{
                                        p: 1,
                                        position: "absolute",
                                        zIndex: 1000,
                                    }}
                                >
                                    <Chip
                                        size="small"
                                        sx={{
                                            fontFamily: "consolas",
                                            background: "rgba(255,255,255,0.8)",
                                        }}
                                        icon={<Image />}
                                        label={`${
                                            entity.Acquisition.Phenostation.name
                                        } ${formatDateTime(
                                            entity.Acquisition
                                                .acquisitionDatetime
                                        )} ${entity.entityRef}`}
                                    />
                                </Box>
                                <TransformComponent
                                    wrapperClass="blueprint-bg"
                                    key={categoryVisuParams}
                                    wrapperStyle={{
                                        borderRadius: 16,
                                        height: "80vh",
                                        aspectRatio: "1/1",
                                    }}
                                    contentStyle={{
                                        width: "1000%",
                                        verticalAlign: "middle",
                                        boxShadow: "0px 2px 15px black",
                                    }}
                                >
                                    <AnnotatedCanvas
                                        imageSrc={presignedUrl}
                                        imageData={imageData}
                                        categoryVisuParams={categoryVisuParams}
                                    />
                                </TransformComponent>
                                <Box
                                    sx={{
                                        width: "100%",
                                        p: 1,
                                        display: "flex",
                                        justifyContent: "center",
                                    }}
                                >
                                    <Controls />
                                </Box>
                            </>
                        </TransformWrapper>
                    </Grid>
                    <Grid item sx={{ p: 1, flexGrow: 1 }}>
                        <Box
                            sx={{
                                backgroundColor: "white",
                                p: 2,
                                borderRadius: 2,
                            }}
                        >
                            <Stack gap={2}>
                                <Autocomplete
                                    fullWidth
                                    size="small"
                                    options={labels}
                                    value={label}
                                    onChange={(_, value) => setLabel(value)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Label"
                                            placeholder="Select a label"
                                        />
                                    )}
                                />
                                <Box sx={{ width: "fit-content" }}>
                                    {categoryVisuParams && (
                                        <CategoryCheckboxGroup
                                            direction="column"
                                            categoryVisuParams={
                                                categoryVisuParams
                                            }
                                            setCategoryVisuParams={
                                                setCategoryVisuParams
                                            }
                                        />
                                    )}
                                </Box>

                                <Link
                                    href={presignedUrl}
                                    target="_blank"
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                    }}
                                >
                                    Raw image <OpenInNew />
                                </Link>
                            </Stack>
                        </Box>
                    </Grid>
                    <Grid item sx={{ p: 1, flexShrink: 1 }}>
                        <IconButton onClick={() => setOpenModal(false)}>
                            <Close />
                        </IconButton>
                    </Grid>
                </Grid>
            </Modal>

            <Grid
                container
                justifyContent="space-between"
                alignItems="flex-start"
                spacing={1}
            >
                <Grid item xs={12}>
                    {categoryVisuParams && (
                        <CategoryCheckboxGroup
                            categoryVisuParams={categoryVisuParams}
                            setCategoryVisuParams={setCategoryVisuParams}
                        />
                    )}
                </Grid>

                <Grid item xs={6}>
                    <Box
                        sx={{
                            cursor: "pointer",
                        }}
                    >
                        <AnnotatedCanvas
                            key={categoryVisuParams}
                            imageSrc={presignedUrl}
                            imageData={imageData}
                            onClick={() => setOpenModal(true)}
                            categoryVisuParams={categoryVisuParams}
                        />
                    </Box>
                </Grid>
                <Grid item xs={6}>
                    <TraitValueTable
                        entity={entity}
                        xTrait={xTrait}
                        yTrait={yTrait}
                    />
                </Grid>
            </Grid>
        </>
    );
}

CategoryCheckboxGroup.propTypes = {
    categoryVisuParams: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            id: PropTypes.number.isRequired,
            checked: PropTypes.bool.isRequired,
            lineWidth: PropTypes.number.isRequired,
            color: PropTypes.string.isRequired,
        })
    ).isRequired,
    setCategoryVisuParams: PropTypes.func.isRequired,
    direction: PropTypes.oneOf(["row", "column"]),
};

function CategoryCheckboxGroup({
    categoryVisuParams,
    setCategoryVisuParams,
    direction,
}) {
    const [colorPickerOpenId, setColorPickerOpenId] = useState(null);

    const handleChange = (event, source) => {
        setCategoryVisuParams((prevCategoryVisuParams) => {
            const idx = prevCategoryVisuParams.findIndex(
                (category) => category.name === event.target.name
            );

            const newCategoryValue = { ...prevCategoryVisuParams[idx] };

            switch (source) {
                case "checkbox":
                    newCategoryValue.checked = event.target.checked;
                    break;
                case "lineWidth":
                    newCategoryValue.lineWidth = event.target.value;
                    break;
                case "color":
                    newCategoryValue.color = event.target.value;
                    break;
                default:
                // Do nothing
            }

            return prevCategoryVisuParams.toSpliced(idx, 1, newCategoryValue);
        });
    };

    return (
        <FormGroup row={direction !== "column"}>
            {categoryVisuParams.map((category) => (
                <LightTooltip
                    key={category.id}
                    PopperProps={{ keepMounted: true }}
                    {...(colorPickerOpenId === category.id
                        ? { open: true }
                        : {})}
                    title={
                        <Stack
                            direction={direction ?? "row"}
                            spacing={1}
                            alignItems="center"
                        >
                            <input
                                type="color"
                                name={category.name}
                                value={category.color}
                                onFocus={() =>
                                    setColorPickerOpenId(category.id)
                                }
                                onBlur={() => setColorPickerOpenId(null)}
                                onChange={(event) =>
                                    handleChange(event, "color")
                                }
                            />
                            <Box
                                sx={{
                                    width: 100,
                                    px: 2,
                                    alignItems: "center",
                                    display: "flex",
                                }}
                            >
                                <Slider
                                    size="small"
                                    name={category.name}
                                    min={1}
                                    max={10}
                                    step={1}
                                    value={category.lineWidth}
                                    onChange={(event) =>
                                        handleChange(event, "lineWidth")
                                    }
                                />
                            </Box>
                        </Stack>
                    }
                >
                    <FormControlLabel
                        label={category.name}
                        control={
                            <Checkbox
                                name={category.name}
                                checked={category.checked}
                                onChange={(event) =>
                                    handleChange(event, "checkbox")
                                }
                                sx={{
                                    color: category.color,
                                    "&.Mui-checked": {
                                        color: category.color,
                                    },
                                }}
                            />
                        }
                    />
                </LightTooltip>
            ))}
        </FormGroup>
    );
}
